/**
 * Pure domain logic for the Control Room "Controlling" tab, the port of the
 * legacy `ufrmControlling.pas` form.
 *
 * The component keeps the view state; these helpers own the date defaults, the
 * penalty totals and the payloads the penalty grid sends, so the rules can be
 * unit-tested without rendering.
 */
import type { UserPenalty } from './salary/userPenalties';

/** A cash drawer handover from `/api/controlling/cash-handovers`. */
export interface CashHandover {
    id: number;
    userId: number | null;
    username: string | null;
    handoverDate: string | null;
    handoverTime: string | null;
    amount: number;
    notes: string | null;
    difference: number;
    isEdited: boolean;
}

/** A stock row held on a date, from `/api/controlling/inventory-at-date`. */
export interface InventoryAtDateItem {
    id: number;
    nr: number | null;
    status: string | null;
    nrContract: string | null;
    denumire: string | null;
    um: string | null;
    cantitate: number | null;
    sumainLei: number | null;
    inv: boolean;
    invFizic: boolean;
    contabilitate: boolean;
    mentiuni: string | null;
    expirationDate: string | null;
}

/** A previous-session verification summarised per cash session, from `/api/controlling/session-verifications`. */
export interface SessionVerificationSummary {
    sessionNumber: number;
    cashPointName: string | null;
    sessionStart: string | null;
    sessionEnd: string | null;
    /** Users who recorded transactions in the verified session. */
    operators: string | null;
    /** Users who re-assessed the session's contracts. */
    verifiers: string | null;
    contractCount: number;
    verifiedCount: number;
    differenceContractCount: number;
    differenceRon: number;
    differenceEur: number;
    firstVerifiedAt: string | null;
    lastVerifiedAt: string | null;
}

/** One contract of a verified session. */
export interface SessionVerificationContract {
    id: number;
    contractId: number;
    contractNumber: string | null;
    objectDescription: string | null;
    loanAmount: number;
    differenceRon: number;
    differenceEur: number;
    notes: string | null;
    verified: boolean;
    verifiedBy: string | null;
    verifiedAt: string | null;
}

/** A verified session with its contracts, from `/api/controlling/session-verifications/:sessionNumber`. */
export interface SessionVerificationDetail {
    session: SessionVerificationSummary;
    contracts: SessionVerificationContract[];
}

/** The file formats the verification reports download in. */
export type ReportFormat = 'xlsx' | 'pdf';

/** An authenticated download: the API path and the name to save under if the server gives none. */
export interface ReportDownload {
    path: string;
    filename: string;
    mimeType: string;
}

/** An inclusive range of calendar days in `YYYY-MM-DD` form. */
export interface DateRange {
    startDate: string;
    endDate: string;
}

/** Penalty totals shown under the penalty grid for the chosen period. */
export interface PenaltyTotals {
    /** Sum of the differences marked "se aplica" (Total DE APLICAT). */
    toApply: number;
    /** Sum of the differences already applied (Total APLICAT). */
    applied: number;
}

/** The two editable flags of a penalty row. */
export type PenaltyFlag = 'seAplica' | 'aplicat';

/** Payload that updates one flag of a penalty through `POST /api/user-penalties`. */
export interface PenaltyFlagUpdate {
    id: number;
    seAplica?: boolean;
    aplicat?: boolean;
    dataAplicare?: string | null;
}

/** Form state of the "Adauga penalizare" dialog. */
export interface PenaltyForm {
    value: number | null;
    description: string;
    date: string;
}

/** Payload that creates a penalty through `POST /api/user-penalties`. */
export interface NewPenaltyPayload {
    userId: number;
    utilizator: string | null;
    diff: number;
    mentiuni: string | null;
    dataAbatere: string;
    casaPredId: number;
}

/**
 * Handover id stored on a manually added penalty; `0` marks "not produced by a
 * handover" exactly as the legacy `btnSaveAddUserPenaltyClick` insert did.
 */
export const MANUAL_PENALTY_HANDOVER_ID = 0;

const padTwo = (value: number): string => String(value).padStart(2, '0');

/** Format a local date as `YYYY-MM-DD`. */
export function toIsoDay(date: Date): string {
    return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;
}

/**
 * Default "Predari casa" period: the first day of the current month up to today,
 * as set by the legacy `tsRapoarteCasaShow` handler.
 */
export function getDefaultHandoverRange(today: Date): DateRange {
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return { startDate: toIsoDay(firstOfMonth), endDate: toIsoDay(today) };
}

const roundToCents = (value: number): number => Math.round(value * 100) / 100;

/**
 * Totals for the selected user's penalties in the period, mirroring the legacy
 * `CalculateUserPenalties` query: "de aplicat" sums the rows marked to apply,
 * "aplicat" sums the rows already applied, independently of each other.
 */
export function calculatePenaltyTotals(
    penalties: Pick<UserPenalty, 'diff' | 'seAplica' | 'aplicat'>[],
): PenaltyTotals {
    let toApply = 0;
    let applied = 0;
    for (const penalty of penalties) {
        const diff = penalty.diff ?? 0;
        if (penalty.seAplica) {
            toApply += diff;
        }
        if (penalty.aplicat) {
            applied += diff;
        }
    }
    return { toApply: roundToCents(toApply), applied: roundToCents(applied) };
}

/**
 * Payload for ticking a penalty flag. Applying a penalty stamps today as its
 * application date and un-applying clears it, like the legacy
 * `zqUserPenaltiesAfterPost` update; the "se aplica" flag leaves the date alone.
 */
export function buildPenaltyFlagUpdate(
    penaltyId: number,
    flag: PenaltyFlag,
    checked: boolean,
    today: Date,
): PenaltyFlagUpdate {
    if (flag === 'seAplica') {
        return { id: penaltyId, seAplica: checked };
    }
    return { id: penaltyId, aplicat: checked, dataAplicare: checked ? toIsoDay(today) : null };
}

/** An empty "Adauga penalizare" form dated today. */
export function createEmptyPenaltyForm(today: Date): PenaltyForm {
    return { value: null, description: '', date: toIsoDay(today) };
}

/** Whether the dialog holds a usable non-zero amount and a date. */
export function canSavePenalty(form: PenaltyForm): boolean {
    return form.value !== null && Number.isFinite(form.value) && form.value !== 0 && !!form.date;
}

/** Build the create payload for a penalty charged to the selected handover's user. */
export function buildNewPenaltyPayload(
    handover: Pick<CashHandover, 'userId' | 'username'>,
    form: PenaltyForm,
): NewPenaltyPayload | null {
    const { value } = form;
    if (handover.userId === null || value === null || !canSavePenalty(form)) {
        return null;
    }
    const description = form.description.trim();
    return {
        userId: handover.userId,
        utilizator: handover.username,
        diff: value,
        mentiuni: description === '' ? null : description,
        dataAbatere: form.date,
        casaPredId: MANUAL_PENALTY_HANDOVER_ID,
    };
}

/** Format a money amount in lei with no decimals, as the Control Room does. */
export function formatLeiWhole(value: number | null | undefined): string {
    const numeric = Number(value);
    const safe = Number.isFinite(numeric) ? numeric : 0;
    return new Intl.NumberFormat('ro-RO', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    }).format(Math.round(safe));
}

/**
 * A contract re-assessment is worth a controller's attention above this RON
 * difference; the Session Verification page highlights the same threshold.
 */
export const SIGNIFICANT_VERIFICATION_DIFFERENCE_RON = 30;

/** Whether a RON difference exceeds the attention threshold, either way. */
export function isSignificantVerificationDifference(differenceRon: number): boolean {
    return Math.abs(differenceRon) > SIGNIFICANT_VERIFICATION_DIFFERENCE_RON;
}

/** "verified / queued" contracts of a session. */
export function formatVerificationProgress(
    session: Pick<SessionVerificationSummary, 'verifiedCount' | 'contractCount'>,
): string {
    return `${session.verifiedCount} / ${session.contractCount}`;
}

const REPORT_MIME_TYPES: Record<ReportFormat, string> = {
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pdf: 'application/pdf',
};

/** Query parameters of the per-session verification list and its export. */
export function buildSessionVerificationParams(
    range: DateRange,
    onlyDifferences: boolean,
): Record<string, string> {
    const params: Record<string, string> = {
        startDate: range.startDate,
        endDate: range.endDate,
    };
    if (onlyDifferences) {
        params.onlyDifferences = 'true';
    }
    return params;
}

/** Download of the per-session verification list of `range`. */
export function buildSessionVerificationsDownload(
    range: DateRange,
    onlyDifferences: boolean,
    format: ReportFormat,
): ReportDownload {
    const search = new URLSearchParams({
        ...buildSessionVerificationParams(range, onlyDifferences),
        format,
    });
    return {
        path: `/controlling/session-verifications/export?${search.toString()}`,
        filename: `verificari_sesiuni_${range.startDate}_${range.endDate}.${format}`,
        mimeType: REPORT_MIME_TYPES[format],
    };
}

/** Download of the contracts of one verified session. */
export function buildSessionVerificationDetailDownload(
    sessionNumber: number,
    format: ReportFormat,
): ReportDownload {
    return {
        path: `/controlling/session-verifications/${sessionNumber}/export?format=${format}`,
        filename: `verificare_sesiune_${sessionNumber}.${format}`,
        mimeType: REPORT_MIME_TYPES[format],
    };
}

/**
 * Calendar day of a date or datetime string, in the shop's local time; empty for
 * missing or unparseable input. Datetimes arrive from the API in UTC, so a
 * penalty recorded just after local midnight must not be shown on the day before.
 */
export function formatDay(value: string | null | undefined): string {
    if (!value) {
        return '';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '' : toIsoDay(parsed);
}
